import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBoxOpen, FaClipboardList, FaEnvelope, FaBriefcase,
  FaSignOutAlt, FaTachometerAlt, FaBlog, FaPlus,
  FaEdit, FaTrash, FaCheck, FaTimes, FaUpload,
  FaEye, FaSearch, FaPhoneAlt,
  FaStar, FaQuoteLeft, FaDownload,
  FaQuestionCircle, FaWhatsapp, FaIndustry,
  FaArrowRight, FaUserShield
} from 'react-icons/fa';
import {
  getProducts, addProduct, updateProduct, deleteProduct,
  getServices, addService, updateService, deleteService,
  getBlogs, addBlog, updateBlog, deleteBlog,
  getEnquiries, updateEnquiryStatus, deleteEnquiry, getCareerApplications, updateCareerStatus, deleteCareerApplication,
  getTestimonials, addTestimonial, updateTestimonial, deleteTestimonial,
  getFAQs, addFAQ, updateFAQ, deleteFAQ
} from '../firebase/db';
import { translateFAQText } from '../utils/faqLocalization';
import { translateProductText } from '../utils/productLocalization';
import { translateBlogText } from '../utils/blogLocalization';
import Logo from '../components/Logo';
import './Admin.css';

// ====================== AUTH ======================
const requireAuth = (navigate) => {
  if (!localStorage.getItem('adminAuth')) navigate('/admin');
};

// ====================== MODAL WRAPPER ======================
const Modal = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

// ====================== ADMIN LAYOUT ======================
const AdminLayout = ({ children, activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const handleLogout = () => { localStorage.removeItem('adminAuth'); navigate('/admin'); };

  const menu = [
    { id: 'dashboard', name: 'Dashboard', icon: <FaTachometerAlt /> },
    { id: 'products', name: 'Products', icon: <FaBoxOpen /> },
    { id: 'services', name: 'Services', icon: <FaClipboardList /> },
    { id: 'blogs', name: 'Blogs', icon: <FaBlog /> },
    { id: 'testimonials', name: 'Testimonials', icon: <FaQuoteLeft /> },
    { id: 'faqs', name: 'FAQs', icon: <FaQuestionCircle /> },
    { id: 'product_enquiries', name: 'Product Enquiry', icon: <FaIndustry /> },
    { id: 'enquiries', name: 'General Enquiry', icon: <FaEnvelope /> },
    { id: 'careers', name: 'Careers', icon: <FaBriefcase /> },
  ];

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <div className="sidebar-logo-wrap"><Logo width="140px" height="44px" isLight={true} /></div>
          <span className="sidebar-tag">Admin Panel</span>
        </div>
        <ul className="admin-menu">
          {menu.map(item => (
            <li
              key={item.id}
              className={activeTab === item.id ? 'active' : ''}
              onClick={() => setActiveTab(item.id)}
            >
              <span className="menu-icon-wrap">{item.icon}</span>
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
        <button onClick={handleLogout} className="admin-logout-btn">
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      <main className="admin-content">
        <header className="admin-header">
          <h2 className="admin-header-title">
            <span className="header-title-icon">{menu.find(m => m.id === activeTab)?.icon}</span>
            <span>{menu.find(m => m.id === activeTab)?.name}</span>
          </h2>
          <span className="admin-header-user">
            <FaUserShield style={{ color: '#04416d' }} /> digvijayindustries@gmail.com
          </span>
        </header>
        <div className="admin-body">{children}</div>
      </main>
    </div>
  );
};

// ====================== CRUD TABLE WITH PAGINATION ======================
const ITEMS_PER_PAGE = 7;
const CrudTable = ({ items, columns, onEdit, onDelete, loading, customActions, emptyText, actionsWidth }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const filtered = items.filter(item =>
    columns.some(c => String(item[c.key] || '').toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [search]);

  if (loading) return (
    <div className="table-skeleton">
      {[1, 2, 3].map(i => <div key={i} className="skeleton-row" />)}
    </div>
  );
  if (!items.length) return (
    <div className="admin-empty">
      <FaBoxOpen />
      <p>{emptyText || "No items yet. Click the button above to add one!"}</p>
    </div>
  );

  return (
    <div className="admin-table-wrap">
      <div className="table-toolbar">
        <div className="table-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span className="table-count">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="table-responsive">
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} style={c.width ? { width: c.width } : {}}>
                  {c.label}
                </th>
              ))}
              <th style={actionsWidth ? { width: actionsWidth } : {}} className="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map(item => (
              <tr key={item.id}>
                {columns.map(c => (
                  <td key={c.key} style={c.width ? { width: c.width } : {}}>
                    {c.render ? c.render(item)
                      : c.key === 'image' || c.key === 'images'
                        ? <img src={item.images?.[0] || item.image || 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=80'} alt="" className="table-thumb" />
                        : c.key === 'resume'
                          ? (item[c.key] ? <a href={item[c.key]} target="_blank" rel="noreferrer" download style={{ color: '#04416d', fontWeight: 'bold' }}><FaDownload /> Download PDF</a> : <span>-</span>)
                          : <span className="cell-text">{String(item[c.key] || '-').substring(0, 60)}</span>}
                  </td>
                ))}
                <td className="action-cell" style={actionsWidth ? { width: actionsWidth } : {}}>
                  <div className="action-btns">
                    {customActions ? customActions(item) : (
                      <>
                        <button className="btn-icon edit" onClick={() => onEdit(item)} title="Edit"><FaEdit /></button>
                        <button className="btn-icon del" onClick={() => onDelete(item.id)} title="Delete"><FaTrash /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label="Page navigation" className="pagination-wrapper">
          <ul className="pagination">
            <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(safePage - 1)}>Previous</button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${safePage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(safePage + 1)}>Next</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

// ====================== PRODUCT MANAGER ======================
const PRODUCT_CATEGORIES = [
  'Food Processing',
  'Agricultural Automation',
  'Heating Systems',
  'Material Handling',
  'Air Handling'
];

const ProductManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', category: '', capacity: '', shortDescription: '', fullDescription: '',
    name_mr: '', capacity_mr: '', shortDescription_mr: '', fullDescription_mr: '',
    name_kn: '', capacity_kn: '', shortDescription_kn: '', fullDescription_kn: '',
    images: [], featured: false
  });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const refresh = useCallback(() => { setLoading(true); getProducts().then(d => { setItems(d); setLoading(false); }); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      name: '', category: '', capacity: '', shortDescription: '', fullDescription: '',
      name_mr: '', capacity_mr: '', shortDescription_mr: '', fullDescription_mr: '',
      name_kn: '', capacity_kn: '', shortDescription_kn: '', fullDescription_kn: '',
      images: [], featured: false
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || '',
      category: item.category || '',
      capacity: item.capacity || '',
      shortDescription: item.shortDescription || '',
      fullDescription: item.fullDescription || '',
      name_mr: item.name_mr || '',
      capacity_mr: item.capacity_mr || '',
      shortDescription_mr: item.shortDescription_mr || '',
      fullDescription_mr: item.fullDescription_mr || '',
      name_kn: item.name_kn || '',
      capacity_kn: item.capacity_kn || '',
      shortDescription_kn: item.shortDescription_kn || '',
      fullDescription_kn: item.fullDescription_kn || '',
      images: item.images || [],
      featured: item.featured || false
    });
    setModalOpen(true);
  };

  const handleAutoTranslate = async () => {
    if (!form.name && !form.shortDescription && !form.fullDescription) {
      alert('Please enter at least Product Name or Description first in English.');
      return;
    }
    setTranslating(true);
    try {
      const name_mr = form.name ? await translateProductText(form.name, 'mr') : '';
      const name_kn = form.name ? await translateProductText(form.name, 'kn') : '';
      const cap_mr = form.capacity ? await translateProductText(form.capacity, 'mr') : '';
      const cap_kn = form.capacity ? await translateProductText(form.capacity, 'kn') : '';
      const sDesc_mr = form.shortDescription ? await translateProductText(form.shortDescription, 'mr') : '';
      const sDesc_kn = form.shortDescription ? await translateProductText(form.shortDescription, 'kn') : '';
      const fDesc_mr = form.fullDescription ? await translateProductText(form.fullDescription, 'mr') : '';
      const fDesc_kn = form.fullDescription ? await translateProductText(form.fullDescription, 'kn') : '';

      setForm(prev => ({
        ...prev,
        name_mr: prev.name_mr || name_mr,
        name_kn: prev.name_kn || name_kn,
        capacity_mr: prev.capacity_mr || cap_mr,
        capacity_kn: prev.capacity_kn || cap_kn,
        shortDescription_mr: prev.shortDescription_mr || sDesc_mr,
        shortDescription_kn: prev.shortDescription_kn || sDesc_kn,
        fullDescription_mr: prev.fullDescription_mr || fDesc_mr,
        fullDescription_kn: prev.fullDescription_kn || fDesc_kn
      }));
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let finalForm = { ...form };

    // Auto-translate any missing Marathi / Kannada fields before saving
    if (finalForm.name) {
      if (!finalForm.name_mr) finalForm.name_mr = await translateProductText(finalForm.name, 'mr');
      if (!finalForm.name_kn) finalForm.name_kn = await translateProductText(finalForm.name, 'kn');
    }
    if (finalForm.capacity) {
      if (!finalForm.capacity_mr) finalForm.capacity_mr = await translateProductText(finalForm.capacity, 'mr');
      if (!finalForm.capacity_kn) finalForm.capacity_kn = await translateProductText(finalForm.capacity, 'kn');
    }
    if (finalForm.shortDescription) {
      if (!finalForm.shortDescription_mr) finalForm.shortDescription_mr = await translateProductText(finalForm.shortDescription, 'mr');
      if (!finalForm.shortDescription_kn) finalForm.shortDescription_kn = await translateProductText(finalForm.shortDescription, 'kn');
    }
    if (finalForm.fullDescription) {
      if (!finalForm.fullDescription_mr) finalForm.fullDescription_mr = await translateProductText(finalForm.fullDescription, 'mr');
      if (!finalForm.fullDescription_kn) finalForm.fullDescription_kn = await translateProductText(finalForm.fullDescription, 'kn');
    }

    if (editing) await updateProduct(editing, finalForm);
    else await addProduct(finalForm);
    setSaving(false);
    setModalOpen(false);
    refresh();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      alert('Image size must be less than 1MB. Please compress your image and try again.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setForm(f => ({ ...f, images: [reader.result] }));
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">Products Catalog</h3>
          <p className="section-desc">Manage products in English, Marathi, and Kannada. Automatically translated for visitors in all languages.</p>
        </div>
        <div className="section-bar-actions">
          <button className="btn-add" onClick={openAdd}><FaPlus /> Add Product</button>
          <span className="badge-count">{items.length} Products</span>
        </div>
      </div>
      <CrudTable
        items={items} loading={loading}
        columns={[{ key: 'images', label: 'Image' }, { key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'capacity', label: 'Capacity' }]}
        onEdit={openEdit}
        onDelete={async id => { if (window.confirm('Delete this product?')) { await deleteProduct(id); refresh(); } }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Product' : 'Add New Product'}>
        <form onSubmit={handleSubmit} className="modal-form">
          {/* English Section (Primary) */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#1e293b' }}>🇬🇧 English (Primary)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>Product Name (English) *</label>
                <input className="mf-control" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Automatic Raisin Processing Machine" />
              </div>
              <div className="mf-group">
                <label>Category</label>
                <select className="mf-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="">Select Category</option>
                  {PRODUCT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
            </div>
            <div className="mf-row">
              <div className="mf-group">
                <label>Capacity (English)</label>
                <input className="mf-control" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="e.g. 500 KG/HR or 1 to 4 TON/HR" />
              </div>
              <div className="mf-group">
                <label>Product Image</label>
                <div className="file-upload-wrapper">
                  <input type="file" accept="image/*" onChange={handleImageChange} id="prod-img" />
                  <label htmlFor="prod-img" className="file-upload-btn"><FaUpload /> Upload</label>
                  {form.images[0] && <img src={form.images[0]} alt="preview" className="img-preview" />}
                </div>
              </div>
            </div>
            <div className="mf-group">
              <label>Short Description (English)</label>
              <textarea className="mf-control" rows={2} value={form.shortDescription} onChange={e => setForm(f => ({ ...f, shortDescription: e.target.value }))} placeholder="Brief summary of the product for catalog cards..." />
            </div>
            <div className="mf-group">
              <label>Full Description (English)</label>
              <textarea className="mf-control" rows={4} value={form.fullDescription} onChange={e => setForm(f => ({ ...f, fullDescription: e.target.value }))} placeholder="Full specifications and details for detail page..." />
            </div>
            <div className="mf-check" style={{ marginTop: '10px' }}>
              <input type="checkbox" id="feat" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} />
              <label htmlFor="feat">Show on Home Page (Featured)</label>
            </div>
          </div>

          {/* Auto Translate Action Button */}
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 20px',
                borderRadius: '8px',
                background: '#e0f2fe',
                color: '#0369a1',
                border: '1px solid #bae6fd',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              {translating ? 'Translating Product...' : '✨ Auto-Translate to Marathi & Kannada'}
            </button>
            <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
              (Note: If left empty, translations are automatically generated on Save)
            </p>
          </div>

          {/* Marathi Section */}
          <div style={{ background: '#fefce8', padding: '16px', borderRadius: '12px', border: '1px solid #fef08a', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#854d0e' }}>🚩 मराठी (Marathi)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>उत्पादनाचे नाव (मराठी)</label>
                <input className="mf-control" value={form.name_mr} onChange={e => setForm(f => ({ ...f, name_mr: e.target.value }))} placeholder="मराठीत नाव (किंवा आपोआप भाषांतरित होईल)" />
              </div>
              <div className="mf-group">
                <label>क्षमता (मराठी)</label>
                <input className="mf-control" value={form.capacity_mr} onChange={e => setForm(f => ({ ...f, capacity_mr: e.target.value }))} placeholder="उदा. ५०० किलो प्रति तास" />
              </div>
            </div>
            <div className="mf-group">
              <label>संक्षिप्त माहिती (मराठी)</label>
              <textarea className="mf-control" rows={2} value={form.shortDescription_mr} onChange={e => setForm(f => ({ ...f, shortDescription_mr: e.target.value }))} placeholder="कॅटलॉग कार्डसाठी संक्षिप्त माहिती..." />
            </div>
            <div className="mf-group">
              <label>संपूर्ण माहिती (मराठी)</label>
              <textarea className="mf-control" rows={3} value={form.fullDescription_mr} onChange={e => setForm(f => ({ ...f, fullDescription_mr: e.target.value }))} placeholder="तपशीलवार वर्णन..." />
            </div>
          </div>

          {/* Kannada Section */}
          <div style={{ background: '#fdf2f8', padding: '16px', borderRadius: '12px', border: '1px solid #fbcfe8', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#9d174d' }}>🟡 ಕನ್ನಡ (Kannada)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>ಉತ್ಪನ್ನದ ಹೆಸರು (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.name_kn} onChange={e => setForm(f => ({ ...f, name_kn: e.target.value }))} placeholder="ಕನ್ನಡದಲ್ಲಿ ಹೆಸರು (ಅಥವಾ ಸ್ವಯಂ ಅನುವಾದಗೊಳ್ಳುತ್ತದೆ)" />
              </div>
              <div className="mf-group">
                <label>ಸಾಮರ್ಥ್ಯ (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.capacity_kn} onChange={e => setForm(f => ({ ...f, capacity_kn: e.target.value }))} placeholder="ಉದಾ. ೫೦೦ ಕೆಜಿ ಪ್ರತಿ ಗಂಟೆಗೆ" />
              </div>
            </div>
            <div className="mf-group">
              <label>ಸಂಕ್ಷಿಪ್ತ ವಿವರ (ಕನ್ನಡ)</label>
              <textarea className="mf-control" rows={2} value={form.shortDescription_kn} onChange={e => setForm(f => ({ ...f, shortDescription_kn: e.target.value }))} placeholder="ಕ್ಯಾಟಲಾಗ್ ಕಾರ್ಡ್‌ಗಳಿಗಾಗಿ ಸಂಕ್ಷಿಪ್ತ ವಿವರ..." />
            </div>
            <div className="mf-group">
              <label>ಪೂರ್ಣ ವಿವರ (ಕನ್ನಡ)</label>
              <textarea className="mf-control" rows={3} value={form.fullDescription_kn} onChange={e => setForm(f => ({ ...f, fullDescription_kn: e.target.value }))} placeholder="ಉತ್ಪನ್ನದ ಸಂಪೂರ್ಣ ವಿವರಣೆ..." />
            </div>
          </div>

          <div className="mf-footer">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving Product...' : (editing ? <><FaCheck /> Update Product</> : <><FaPlus /> Add Product</>)}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ====================== SERVICE MANAGER ======================
const ServiceManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', icon: 'FaTools' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const iconOptions = ['FaTools', 'FaHeadset', 'FaWrench', 'FaCogs', 'FaShippingFast', 'FaAward', 'FaCheckCircle', 'FaIndustry'];

  const refresh = useCallback(() => { setLoading(true); getServices().then(d => { setItems(d); setLoading(false); }); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => { setEditing(null); setForm({ title: '', description: '', icon: 'FaTools' }); setModalOpen(true); };
  const openEdit = (item) => { setEditing(item.id); setForm({ title: item.title || '', description: item.description || '', icon: item.icon || 'FaTools' }); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    if (editing) await updateService(editing, form);
    else await addService(form);
    setSaving(false); setModalOpen(false); refresh();
  };

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">Services Management</h3>
          <p className="section-desc">Manage services displayed on the website Services page.</p>
        </div>
        <div className="section-bar-actions">
          <button className="btn-add" onClick={openAdd}><FaPlus /> Add Service</button>
          <span className="badge-count">{items.length} Services</span>
        </div>
      </div>
      <CrudTable
        items={items} loading={loading}
        columns={[{ key: 'icon', label: 'Icon' }, { key: 'title', label: 'Title' }, { key: 'description', label: 'Description' }]}
        onEdit={openEdit}
        onDelete={async id => { if (window.confirm('Delete?')) { await deleteService(id); refresh(); } }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Service' : 'Add New Service'}>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="mf-row">
            <div className="mf-group">
              <label>Service Title *</label>
              <input className="mf-control" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. 24-Hour Support" />
            </div>
            <div className="mf-group">
              <label>Icon</label>
              <select className="mf-control" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}>
                {iconOptions.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <div className="mf-group">
            <label>Description</label>
            <textarea className="mf-control" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="mf-footer">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving...' : (editing ? <><FaCheck /> Update</> : <><FaPlus /> Add Service</>)}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ====================== BLOG MANAGER ======================
const BlogManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', excerpt: '', content: '', image: '', author: 'Digvijay Industries Team', category: 'Industry Insights', tags: '',
    title_mr: '', excerpt_mr: '', content_mr: '', author_mr: 'दिग्विजय इंडस्ट्रीज टीम', category_mr: 'उद्योग माहिती व ट्रेंड्स', tags_mr: '',
    title_kn: '', excerpt_kn: '', content_kn: '', author_kn: 'ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ತಂಡ', category_kn: 'ಉದ್ಯಮದ ಒಳನೋಟಗಳು', tags_kn: ''
  });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const refresh = useCallback(() => { setLoading(true); getBlogs().then(d => { setItems(d); setLoading(false); }); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      title: '', excerpt: '', content: '', image: '', author: 'Digvijay Industries Team', category: 'Industry Insights', tags: '',
      title_mr: '', excerpt_mr: '', content_mr: '', author_mr: 'दिग्विजय इंडस्ट्रीज टीम', category_mr: 'उद्योग माहिती व ट्रेंड्स', tags_mr: '',
      title_kn: '', excerpt_kn: '', content_kn: '', author_kn: 'ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ತಂಡ', category_kn: 'ಉದ್ಯಮದ ಒಳನೋಟಗಳು', tags_kn: ''
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      title: item.title || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      image: item.image || '',
      author: item.author || 'Digvijay Industries Team',
      category: item.category || 'Industry Insights',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),

      title_mr: item.title_mr || '',
      excerpt_mr: item.excerpt_mr || '',
      content_mr: item.content_mr || '',
      author_mr: item.author_mr || 'दिग्विजय इंडस्ट्रीज टीम',
      category_mr: item.category_mr || '',
      tags_mr: Array.isArray(item.tags_mr) ? item.tags_mr.join(', ') : (item.tags_mr || ''),

      title_kn: item.title_kn || '',
      excerpt_kn: item.excerpt_kn || '',
      content_kn: item.content_kn || '',
      author_kn: item.author_kn || 'ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ತಂಡ',
      category_kn: item.category_kn || '',
      tags_kn: Array.isArray(item.tags_kn) ? item.tags_kn.join(', ') : (item.tags_kn || '')
    });
    setModalOpen(true);
  };

  const handleAutoTranslate = async () => {
    if (!form.title && !form.excerpt && !form.content) {
      alert('Please enter at least Blog Title, Excerpt or Content in English first.');
      return;
    }
    setTranslating(true);
    try {
      const title_mr = form.title ? await translateBlogText(form.title, 'mr') : '';
      const title_kn = form.title ? await translateBlogText(form.title, 'kn') : '';
      const excerpt_mr = form.excerpt ? await translateBlogText(form.excerpt, 'mr') : '';
      const excerpt_kn = form.excerpt ? await translateBlogText(form.excerpt, 'kn') : '';
      const content_mr = form.content ? await translateBlogText(form.content, 'mr') : '';
      const content_kn = form.content ? await translateBlogText(form.content, 'kn') : '';
      const category_mr = form.category ? await translateBlogText(form.category, 'mr') : '';
      const category_kn = form.category ? await translateBlogText(form.category, 'kn') : '';
      const tags_mr = form.tags ? await translateBlogText(form.tags, 'mr') : '';
      const tags_kn = form.tags ? await translateBlogText(form.tags, 'kn') : '';

      setForm(prev => ({
        ...prev,
        title_mr: prev.title_mr || title_mr,
        title_kn: prev.title_kn || title_kn,
        excerpt_mr: prev.excerpt_mr || excerpt_mr,
        excerpt_kn: prev.excerpt_kn || excerpt_kn,
        content_mr: prev.content_mr || content_mr,
        content_kn: prev.content_kn || content_kn,
        category_mr: prev.category_mr || category_mr,
        category_kn: prev.category_kn || category_kn,
        tags_mr: prev.tags_mr || tags_mr,
        tags_kn: prev.tags_kn || tags_kn
      }));
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setTranslating(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      alert('Image size must be less than 1MB. Please compress your image and try again.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setForm(f => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let finalForm = { ...form };

    // Automatically translate any missing Marathi or Kannada fields before saving
    if (finalForm.title) {
      if (!finalForm.title_mr) finalForm.title_mr = await translateBlogText(finalForm.title, 'mr');
      if (!finalForm.title_kn) finalForm.title_kn = await translateBlogText(finalForm.title, 'kn');
    }
    if (finalForm.excerpt) {
      if (!finalForm.excerpt_mr) finalForm.excerpt_mr = await translateBlogText(finalForm.excerpt, 'mr');
      if (!finalForm.excerpt_kn) finalForm.excerpt_kn = await translateBlogText(finalForm.excerpt, 'kn');
    }
    if (finalForm.content) {
      if (!finalForm.content_mr) finalForm.content_mr = await translateBlogText(finalForm.content, 'mr');
      if (!finalForm.content_kn) finalForm.content_kn = await translateBlogText(finalForm.content, 'kn');
    }
    if (finalForm.category) {
      if (!finalForm.category_mr) finalForm.category_mr = await translateBlogText(finalForm.category, 'mr');
      if (!finalForm.category_kn) finalForm.category_kn = await translateBlogText(finalForm.category, 'kn');
    }
    if (finalForm.tags) {
      if (!finalForm.tags_mr) finalForm.tags_mr = await translateBlogText(finalForm.tags, 'mr');
      if (!finalForm.tags_kn) finalForm.tags_kn = await translateBlogText(finalForm.tags, 'kn');
    }

    const tagsArr = typeof finalForm.tags === 'string'
      ? finalForm.tags.split(',').map(t => t.trim()).filter(Boolean)
      : (finalForm.tags || []);
    const tagsMrArr = typeof finalForm.tags_mr === 'string'
      ? finalForm.tags_mr.split(',').map(t => t.trim()).filter(Boolean)
      : (finalForm.tags_mr || []);
    const tagsKnArr = typeof finalForm.tags_kn === 'string'
      ? finalForm.tags_kn.split(',').map(t => t.trim()).filter(Boolean)
      : (finalForm.tags_kn || []);

    const dataToSave = {
      ...finalForm,
      tags: tagsArr,
      tags_mr: tagsMrArr,
      tags_kn: tagsKnArr
    };

    if (editing) await updateBlog(editing, dataToSave);
    else await addBlog(dataToSave);

    setSaving(false);
    setModalOpen(false);
    refresh();
  };

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">Blog Articles</h3>
          <p className="section-desc">Publish and manage blog posts shown on the Blog page (Supports English, Marathi & Kannada).</p>
        </div>
        <div className="section-bar-actions">
          <button className="btn-add" onClick={openAdd}><FaPlus /> Publish Blog</button>
          <span className="badge-count">{items.length} Posts</span>
        </div>
      </div>
      <CrudTable
        items={items} loading={loading}
        columns={[{ key: 'image', label: 'Cover' }, { key: 'title', label: 'Title' }, { key: 'category', label: 'Category' }, { key: 'date', label: 'Date' }]}
        onEdit={openEdit}
        onDelete={async id => { if (window.confirm('Delete this blog post?')) { await deleteBlog(id); refresh(); } }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Blog Post' : 'New Blog Post'}>
        <form onSubmit={handleSubmit} className="modal-form">
          {/* English Section */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#04416d' }}>🌐 English (Original)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>Title *</label>
                <input className="mf-control" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Blog post title" />
              </div>
              <div className="mf-group">
                <label>Category</label>
                <input className="mf-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Industry Insights, Food Processing" />
              </div>
            </div>
            <div className="mf-row">
              <div className="mf-group">
                <label>Author</label>
                <input className="mf-control" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} />
              </div>
              <div className="mf-group">
                <label>Tags (comma-separated)</label>
                <input className="mf-control" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Agriculture, Quality, Machinery" />
              </div>
            </div>
            <div className="mf-group">
              <label>Cover Image (upload or URL)</label>
              <div className="file-upload-wrapper">
                <input type="file" accept="image/*" onChange={handleImageChange} id="blog-img" />
                <label htmlFor="blog-img" className="file-upload-btn"><FaUpload /> Upload</label>
                {form.image && <img src={form.image} alt="preview" className="img-preview" />}
              </div>
              <input className="mf-control" style={{ marginTop: 8 }} value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} placeholder="Or paste image URL..." />
            </div>
            <div className="mf-group">
              <label>Short Excerpt</label>
              <textarea className="mf-control" rows={2} value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Summary shown on listing cards..." />
            </div>
            <div className="mf-group">
              <label>Full Content</label>
              <textarea className="mf-control" rows={5} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Full article content... Use **bold** for bold text." />
            </div>
          </div>

          {/* Auto Translate Action Button */}
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 20px',
                borderRadius: '8px',
                background: '#e0f2fe',
                color: '#0369a1',
                border: '1px solid #bae6fd',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              {translating ? 'Translating Article...' : '✨ Auto-Translate to Marathi & Kannada'}
            </button>
            <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
              (Note: If left empty, translations are automatically generated on Publish)
            </p>
          </div>

          {/* Marathi Section */}
          <div style={{ background: '#fefce8', padding: '16px', borderRadius: '12px', border: '1px solid #fef08a', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#854d0e' }}>🚩 मराठी (Marathi)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>ब्लॉगचे शीर्षक (मराठी)</label>
                <input className="mf-control" value={form.title_mr} onChange={e => setForm(f => ({ ...f, title_mr: e.target.value }))} placeholder="मराठीत शीर्षक (किंवा आपोआप भाषांतरित होईल)" />
              </div>
              <div className="mf-group">
                <label>वर्ग / कॅटेगरी (मराठी)</label>
                <input className="mf-control" value={form.category_mr} onChange={e => setForm(f => ({ ...f, category_mr: e.target.value }))} placeholder="उदा. उद्योग माहिती व ट्रेंड्स" />
              </div>
            </div>
            <div className="mf-row">
              <div className="mf-group">
                <label>लेखक (मराठी)</label>
                <input className="mf-control" value={form.author_mr} onChange={e => setForm(f => ({ ...f, author_mr: e.target.value }))} placeholder="उदा. दिग्विजय इंडस्ट्रीज टीम" />
              </div>
              <div className="mf-group">
                <label>टॅग्ज (मराठी)</label>
                <input className="mf-control" value={form.tags_mr} onChange={e => setForm(f => ({ ...f, tags_mr: e.target.value }))} placeholder="अन्न प्रक्रिया, मशिनरी" />
              </div>
            </div>
            <div className="mf-group">
              <label>संक्षिप्त सारांश (मराठी)</label>
              <textarea className="mf-control" rows={2} value={form.excerpt_mr} onChange={e => setForm(f => ({ ...f, excerpt_mr: e.target.value }))} placeholder="ब्लॉग कार्डसाठी संक्षिप्त सारांश..." />
            </div>
            <div className="mf-group">
              <label>संपूर्ण लेख मजकूर (मराठी)</label>
              <textarea className="mf-control" rows={5} value={form.content_mr} onChange={e => setForm(f => ({ ...f, content_mr: e.target.value }))} placeholder="मराठीत संपूर्ण लेख..." />
            </div>
          </div>

          {/* Kannada Section */}
          <div style={{ background: '#fdf2f8', padding: '16px', borderRadius: '12px', border: '1px solid #fbcfe8', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.98rem', color: '#9d174d' }}>🟡 ಕನ್ನಡ (Kannada)</h4>
            <div className="mf-row">
              <div className="mf-group">
                <label>ಬ್ಲಾಗ್ ಶೀರ್ಷಿಕೆ (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.title_kn} onChange={e => setForm(f => ({ ...f, title_kn: e.target.value }))} placeholder="ಕನ್ನಡದಲ್ಲಿ ಶೀರ್ಷಿಕೆ (ಅಥವಾ ಸ್ವಯಂ ಅನುವಾದಗೊಳ್ಳುತ್ತದೆ)" />
              </div>
              <div className="mf-group">
                <label>ವರ್ಗ / ವರ್ಗೀಕರಣ (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.category_kn} onChange={e => setForm(f => ({ ...f, category_kn: e.target.value }))} placeholder="ಉದಾ. ಉದ್ಯಮದ ಒಳನೋಟಗಳು" />
              </div>
            </div>
            <div className="mf-row">
              <div className="mf-group">
                <label>ಲೇಖಕರು (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.author_kn} onChange={e => setForm(f => ({ ...f, author_kn: e.target.value }))} placeholder="ಉದಾ. ದಿಗ್ವಿಜಯ್ ಇಂಡಸ್ಟ್ರೀಸ್ ತಂಡ" />
              </div>
              <div className="mf-group">
                <label>ಟ್ಯಾಗ್‌ಗಳು (ಕನ್ನಡ)</label>
                <input className="mf-control" value={form.tags_kn} onChange={e => setForm(f => ({ ...f, tags_kn: e.target.value }))} placeholder="ಆಹಾರ ಸಂಸ್ಕರಣೆ, ಯಂತ್ರೋಪಕರಣ" />
              </div>
            </div>
            <div className="mf-group">
              <label>ಸಂಕ್ಷಿಪ್ತ ಸಾರಾಂಶ (ಕನ್ನಡ)</label>
              <textarea className="mf-control" rows={2} value={form.excerpt_kn} onChange={e => setForm(f => ({ ...f, excerpt_kn: e.target.value }))} placeholder="ಬ್ಲಾಗ್ ಕಾರ್ಡ್‌ಗಳಿಗಾಗಿ ಸಂಕ್ಷಿಪ್ತ ಸಾರಾಂಶ..." />
            </div>
            <div className="mf-group">
              <label>ಪೂರ್ಣ ಲೇಖನದ ವಿವರ (ಕನ್ನಡ)</label>
              <textarea className="mf-control" rows={5} value={form.content_kn} onChange={e => setForm(f => ({ ...f, content_kn: e.target.value }))} placeholder="ಕನ್ನಡದಲ್ಲಿ ಸಂಪೂರ್ಣ ಲೇಖನ..." />
            </div>
          </div>

          <div className="mf-footer">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Publishing...' : (editing ? <><FaCheck /> Update Blog</> : <><FaPlus /> Publish Blog</>)}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ====================== PRODUCT ENQUIRIES MANAGER ======================
const ProductEnquiriesManager = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedProductFilter, setSelectedProductFilter] = useState('ALL');

  const fetchEnquiries = useCallback(() => {
    setLoading(true);
    getEnquiries().then(d => {
      // Filter out to only product consultation enquiries
      const prods = d.filter(e => e.productName || e.type === 'product' || e.enquiryType === 'product');
      setEnquiries(prods);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusChange = async (id, status) => {
    await updateEnquiryStatus(id, status);
    fetchEnquiries();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product enquiry?')) {
      await deleteEnquiry(id);
      fetchEnquiries();
    }
  };

  const uniqueProducts = Array.from(
    new Set(enquiries.map(e => e.productName).filter(Boolean))
  );

  const displayedEnquiries = (selectedProductFilter === 'ALL'
    ? enquiries
    : enquiries.filter(e => e.productName === selectedProductFilter)
  ).map(e => ({
    ...e,
    formattedDate: e.createdAt ? new Date(e.createdAt).toLocaleDateString('en-IN') : 'New'
  }));

  const columns = [
    {
      key: 'productName',
      label: 'Product / Machine',
      width: '25%',
      render: (item) => (
        <div className="admin-prod-cell">
          <span className="admin-prod-name">{item.productName || 'Machinery Consultation'}</span>
          <div className="admin-prod-tags">
            {item.productCategory && <span className="admin-prod-badge cat">{item.productCategory}</span>}
            {item.productCapacity && <span className="admin-prod-badge cap">{item.productCapacity}</span>}
          </div>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Customer Name',
      width: '18%',
      render: (item) => (
        <div className="admin-cust-col">
          <span className="admin-cust-name">{item.name}</span>
          <span className={`status-tag-micro status-${String(item.status || 'New').toLowerCase().replace(/\s+/g, '-')}`}>
            {item.status || 'New'}
          </span>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Contact',
      width: '17%',
      render: (item) => (
        <div className="admin-contact-cell">
          <a href={`tel:${item.phone}`} className="admin-phone-link" title="Call Customer">
            <FaPhoneAlt /> {item.phone}
          </a>
          <a
            href={`https://wa.me/${String(item.phone).replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${item.name}, thank you for inquiring about ${item.productName || 'our machinery'} at Digvijay Industries.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-wa-direct"
            title="Chat on WhatsApp"
          >
            <FaWhatsapp />
          </a>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      width: '20%',
      render: (item) => item.email ? <span className="admin-email-text" title={item.email}>{item.email}</span> : <span style={{ color: '#94a3b8' }}>—</span>
    },
    {
      key: 'formattedDate',
      label: 'Date',
      width: '8%',
      render: (item) => (
        <span className="admin-date-text">
          {item.formattedDate}
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">Product Enquiries</h3>
          <p className="section-desc">
            All product consultations & quotations submitted through Product Detail pages.
          </p>
        </div>
        <div className="section-bar-actions">
          {uniqueProducts.length > 0 && (
            <div className="filter-group">
              <label htmlFor="prod-filter" className="filter-label">
                Machine:
              </label>
              <select
                id="prod-filter"
                className="mf-control filter-select"
                value={selectedProductFilter}
                onChange={e => setSelectedProductFilter(e.target.value)}
              >
                <option value="ALL">All Machines ({enquiries.length})</option>
                {uniqueProducts.map(pName => (
                  <option key={pName} value={pName}>
                    {pName} ({enquiries.filter(e => e.productName === pName).length})
                  </option>
                ))}
              </select>
            </div>
          )}
          <span className="badge-count">{displayedEnquiries.length} Enquiries</span>
        </div>
      </div>

      <CrudTable
        items={displayedEnquiries}
        loading={loading}
        columns={columns}
        actionsWidth="12%"
        emptyText="No product enquiries yet. When customers submit requests on product pages, they will show here."
        customActions={(item) => {
          const currentStatus = item.status || 'New';
          return (
            <div className="product-action-btns">
              <select
                value={currentStatus}
                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                className={`status-pill-select status-${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}
                title="Change status"
              >
                <option value="New">● New</option>
                <option value="Contacted">● Contacted</option>
                <option value="Interested">● Interested</option>
                <option value="Not Interested">● Not Interested</option>
              </select>
              <button
                type="button"
                className="btn-icon view"
                onClick={() => setSelected(item)}
                title="View Enquiry Details"
              >
                <FaEye />
              </button>
              <button
                type="button"
                className="btn-icon del"
                onClick={() => handleDelete(item.id)}
                title="Delete Enquiry"
              >
                <FaTrash />
              </button>
            </div>
          );
        }}
      />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Product Consultation Details">
        {selected && (
          <div className="enquiry-detail">
            <div className="admin-modal-product-banner">
              <div className="amp-left">
                <span className="amp-badge">Product Inquiry</span>
                <h4>{selected.productName || 'Machinery Consultation'}</h4>
                <div className="amp-meta">
                  {selected.productCategory && <span>Category: <strong>{selected.productCategory}</strong></span>}
                  {selected.productCapacity && <span>Capacity: <strong>{selected.productCapacity}</strong></span>}
                </div>
              </div>
              <div className="amp-right">
                <a
                  href={`https://wa.me/${String(selected.phone).replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${selected.name}, regarding your quotation request for ${selected.productName || 'our machinery'} at Digvijay Industries...`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp-modal"
                >
                  <FaWhatsapp /> WhatsApp Reply
                </a>
              </div>
            </div>

            <div className="eq-row"><label>Customer</label><span><strong>{selected.name}</strong></span></div>
            <div className="eq-row"><label>Contact</label><span><a href={`tel:${selected.phone}`} style={{ color: '#04416d', fontWeight: 600 }}><FaPhoneAlt style={{ marginRight: 6 }} />{selected.phone}</a></span></div>
            <div className="eq-row"><label>Email</label><span>{selected.email || 'Not provided'}</span></div>
            <div className="eq-row"><label>Date</label><span>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN') : '-'}</span></div>
            <div className="eq-row">
              <label>Status</label>
              <div className="modal-status-selector">
                {['New', 'Contacted', 'Interested', 'Not Interested'].map(st => (
                  <button
                    key={st}
                    type="button"
                    className={`btn-status-choice ${st.toLowerCase().replace(/\s+/g, '-')} ${(selected.status || 'New') === st ? 'is-selected' : ''}`}
                    onClick={async () => {
                      await handleStatusChange(selected.id, st);
                      setSelected(prev => ({ ...prev, status: st }));
                    }}
                  >
                    {st === 'Contacted' ? 'Mark as Contacted' : st}
                  </button>
                ))}
              </div>
            </div>
            <div className="eq-row full">
              <label>Plant Location / Requirements / Message</label>
              <p>{selected.message || selected.requirements || 'No additional requirements specified.'}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ====================== GENERAL ENQUIRIES MANAGER ======================
const GeneralEnquiriesManager = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchEnquiries = useCallback(() => {
    setLoading(true);
    getEnquiries().then(d => {
      // Filter out product inquiries to show only general inquiries from Contact page
      const generalList = d.filter(e => !e.productName && e.type !== 'product' && e.enquiryType !== 'product');
      setEnquiries(generalList);
      setLoading(false);
    });
  }, []);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  const handleStatusChange = async (id, status) => {
    await updateEnquiryStatus(id, status);
    fetchEnquiries();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this enquiry?')) {
      await deleteEnquiry(id);
      fetchEnquiries();
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Customer Name',
      width: '24%',
      render: (item) => (
        <div className="admin-cust-col">
          <span className="admin-cust-name">{item.name}</span>
          <span className={`status-tag-micro status-${String(item.status || 'New').toLowerCase().replace(/\s+/g, '-')}`}>
            {item.status || 'New'}
          </span>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      width: '30%',
      render: (item) => item.email ? <span className="admin-email-text" title={item.email}>{item.email}</span> : <span style={{ color: '#94a3b8' }}>—</span>
    },
    {
      key: 'phone',
      label: 'Contact',
      width: '18%',
      render: (item) => (
        <div className="admin-contact-cell">
          <a href={`tel:${item.phone}`} className="admin-phone-link" title="Call Customer">
            <FaPhoneAlt /> {item.phone}
          </a>
          <a
            href={`https://wa.me/${String(item.phone).replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${item.name}, thank you for contacting Digvijay Industries.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-wa-direct"
            title="Chat on WhatsApp"
          >
            <FaWhatsapp />
          </a>
        </div>
      )
    },
    {
      key: 'formattedDate',
      label: 'Date',
      width: '10%',
      render: (item) => (
        <span className="admin-date-text">
          {item.formattedDate}
        </span>
      )
    }
  ];

  const formattedEnquiries = enquiries.map(e => ({
    ...e,
    formattedDate: e.createdAt ? new Date(e.createdAt).toLocaleDateString('en-IN') : 'New'
  }));

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">General Enquiries</h3>
          <p className="section-desc">All general enquiries submitted through the Contact page.</p>
        </div>
        <div className="section-bar-actions">
          <span className="badge-count">{enquiries.length} Total</span>
        </div>
      </div>
      <CrudTable
        items={formattedEnquiries}
        loading={loading}
        columns={columns}
        actionsWidth="18%"
        emptyText="No general enquiries yet."
        customActions={(item) => {
          const currentStatus = item.status || 'New';
          return (
            <div className="product-action-btns">
              <select
                value={currentStatus}
                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                className={`status-pill-select status-${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}
                title="Change status"
              >
                <option value="New">● New</option>
                <option value="Contacted">● Contacted</option>
                <option value="Interested">● Interested</option>
                <option value="Not Interested">● Not Interested</option>
              </select>
              <button
                type="button"
                className="btn-icon view"
                onClick={() => setSelected(item)}
                title="View Details"
              >
                <FaEye />
              </button>
              <button
                type="button"
                className="btn-icon del"
                onClick={() => handleDelete(item.id)}
                title="Delete"
              >
                <FaTrash />
              </button>
            </div>
          );
        }}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title="General Enquiry Details">
        {selected && (
          <div className="enquiry-detail">
            <div className="eq-row"><label>Name</label><span><strong>{selected.name}</strong></span></div>
            <div className="eq-row"><label>Email</label><span>{selected.email || '-'}</span></div>
            <div className="eq-row"><label>Phone</label><span><a href={`tel:${selected.phone}`} style={{ color: '#04416d', fontWeight: 600 }}>{selected.phone}</a></span></div>
            <div className="eq-row"><label>Date</label><span>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN') : '-'}</span></div>
            <div className="eq-row">
              <label>Status</label>
              <div className="modal-status-selector">
                {['New', 'Contacted', 'Interested', 'Not Interested'].map(st => (
                  <button
                    key={st}
                    type="button"
                    className={`btn-status-choice ${st.toLowerCase().replace(/\s+/g, '-')} ${(selected.status || 'New') === st ? 'is-selected' : ''}`}
                    onClick={async () => {
                      await handleStatusChange(selected.id, st);
                      setSelected(prev => ({ ...prev, status: st }));
                    }}
                  >
                    {st === 'Contacted' ? 'Mark as Contacted' : st}
                  </button>
                ))}
              </div>
            </div>
            <div className="eq-row full"><label>Message</label><p>{selected.message || 'No message provided.'}</p></div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ====================== CAREERS MANAGER ======================
const CareersManager = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { getCareerApplications().then(d => { setApps(d); setLoading(false); }); }, []);

  const handleStatusChange = async (id, status) => {
    await updateCareerStatus(id, status);
    getCareerApplications().then(d => setApps(d));
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Contact' },
    { key: 'resume', label: 'Resume PDF' },
    { key: 'createdAt', label: 'Date' },
    {
      key: 'status', label: 'Status', render: (item) => (
        <select
          value={item.status || 'New'}
          onChange={(e) => handleStatusChange(item.id, e.target.value)}
          className="mf-control"
          style={{ padding: '4px', fontSize: '0.9rem' }}
        >
          <option value="New">New</option>
          <option value="Just Seen">Just Seen</option>
          <option value="Contact Kel">Contact Kel</option>
          <option value="Meeting Fix">Meeting Fix</option>
        </select>
      )
    }
  ];

  const formattedApps = apps.map(a => ({
    ...a,
    createdAt: a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-IN') : 'New',
    experience: `${a.experience} yrs`
  }));

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 className="section-title">Job Applications</h3>
          <p className="section-desc">All job applications received through the Career form.</p>
        </div>
        <div className="section-bar-actions">
          <span className="badge-count">{apps.length} Applications</span>
        </div>
      </div>
      <CrudTable
        items={formattedApps} loading={loading}
        columns={columns}
        onEdit={(item) => {
          const orig = apps.find(ap => ap.id === item.id);
          setSelected(orig);
        }}
        onDelete={async id => {
          if (window.confirm('Delete this career application?')) {
            await deleteCareerApplication(id);
            getCareerApplications().then(d => setApps(d));
          }
        }}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Application Details">
        {selected && (
          <div className="enquiry-detail">
            <div className="eq-row"><label>Name</label><span>{selected.name}</span></div>
            <div className="eq-row"><label>Email</label><span>{selected.email}</span></div>
            <div className="eq-row"><label>Phone</label><span>{selected.phone}</span></div>
            <div className="eq-row"><label>Position</label><span><span className="position-badge">{selected.position}</span></span></div>
            <div className="eq-row"><label>Experience</label><span>{selected.experience}</span></div>
            <div className="eq-row"><label>Resume</label><span>{selected.resume ? <a href={selected.resume} target="_blank" rel="noreferrer" download style={{ color: '#04416d', fontWeight: 'bold' }}>Download PDF</a> : 'Not attached'}</span></div>
            <div className="eq-row"><label>Applied On</label><span>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN') : '-'}</span></div>
            <div className="eq-row"><label>Status</label><span>{selected.status || 'New'}</span></div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ====================== TESTIMONIALS MANAGER ======================
const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', text: '', rating: 5 });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => { setLoading(true); getTestimonials().then(d => { setItems(d); setLoading(false); }); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => { setEditing(null); setForm({ name: '', role: '', text: '', rating: 5 }); setModalOpen(true); };
  const openEdit = (item) => { setEditing(item.id); setForm({ name: item.name || '', role: item.role || '', text: item.text || '', rating: item.rating || 5 }); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (editing) await updateTestimonial(editing, form);
    else await addTestimonial(form);
    setSaving(false);
    setModalOpen(false);
    refresh();
  };

  const columns = [
    { key: 'name', label: 'Client Name' },
    { key: 'role', label: 'Role / Company' },
    { key: 'text', label: 'Testimonial' },
    {
      key: 'rating', label: 'Rating', render: (item) => (
        <span style={{ color: '#f59e0b' }}>{[...Array(item.rating || 5)].map((_, i) => <FaStar key={i} />)}</span>
      )
    },
  ];

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 700 }}>Client Testimonials</h3>
          <p className="section-desc" style={{ margin: '4px 0 0 0' }}>Manage client testimonials and reviews shown on the website.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button className="btn-add" onClick={openAdd}><FaPlus /> Add Testimonial</button>
          <span className="badge-count">{items.length} Testimonials</span>
        </div>
      </div>
      <CrudTable
        items={items} loading={loading} columns={columns}
        onEdit={openEdit}
        onDelete={async id => { await deleteTestimonial(id); refresh(); }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Testimonial' : 'Add Testimonial'}>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="mf-row">
            <div className="mf-group">
              <label>Client Name *</label>
              <input className="mf-control" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Client full name" />
            </div>
            <div className="mf-group">
              <label>Role / Company *</label>
              <input className="mf-control" required value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Farmer, Kolhapur" />
            </div>
          </div>
          <div className="mf-group">
            <label>Testimonial Text *</label>
            <textarea className="mf-control" required rows={4} value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="What the client said..." />
          </div>
          <div className="mf-group">
            <label>Rating (1–5 stars)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[1, 2, 3, 4, 5].map(r => (
                <button type="button" key={r} onClick={() => setForm({ ...form, rating: r })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: r <= form.rating ? '#f59e0b' : '#ccc' }}>
                  <FaStar />
                </button>
              ))}
            </div>
          </div>
          <div className="mf-actions">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : <><FaCheck /> {editing ? 'Update' : 'Add'} Testimonial</>}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ====================== FAQ MANAGER ======================
const FAQManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    question: '', answer: '',
    question_mr: '', answer_mr: '',
    question_kn: '', answer_kn: '',
    category: 'General'
  });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const categoryOptions = ['General', 'Services', 'Warranty', 'Export', 'Custom Orders'];

  const refresh = useCallback(() => { setLoading(true); getFAQs().then(d => { setItems(d); setLoading(false); }); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      question: '', answer: '',
      question_mr: '', answer_mr: '',
      question_kn: '', answer_kn: '',
      category: 'General'
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      question: item.question || '',
      answer: item.answer || '',
      question_mr: item.question_mr || '',
      answer_mr: item.answer_mr || '',
      question_kn: item.question_kn || '',
      answer_kn: item.answer_kn || '',
      category: item.category || 'General'
    });
    setModalOpen(true);
  };

  const handleAutoTranslate = async () => {
    if (!form.question && !form.answer) {
      alert('Please enter at least an English Question or Answer first.');
      return;
    }
    setTranslating(true);
    try {
      const q_mr = form.question ? await translateFAQText(form.question, 'mr') : '';
      const a_mr = form.answer ? await translateFAQText(form.answer, 'mr') : '';
      const q_kn = form.question ? await translateFAQText(form.question, 'kn') : '';
      const a_kn = form.answer ? await translateFAQText(form.answer, 'kn') : '';
      setForm(prev => ({
        ...prev,
        question_mr: prev.question_mr || q_mr,
        answer_mr: prev.answer_mr || a_mr,
        question_kn: prev.question_kn || q_kn,
        answer_kn: prev.answer_kn || a_kn
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let finalForm = { ...form };
    // Automatically translate empty Marathi or Kannada fields
    if (finalForm.question) {
      if (!finalForm.question_mr) finalForm.question_mr = await translateFAQText(finalForm.question, 'mr');
      if (!finalForm.question_kn) finalForm.question_kn = await translateFAQText(finalForm.question, 'kn');
    }
    if (finalForm.answer) {
      if (!finalForm.answer_mr) finalForm.answer_mr = await translateFAQText(finalForm.answer, 'mr');
      if (!finalForm.answer_kn) finalForm.answer_kn = await translateFAQText(finalForm.answer, 'kn');
    }

    if (editing) await updateFAQ(editing, finalForm);
    else await addFAQ(finalForm);
    setSaving(false);
    setModalOpen(false);
    refresh();
  };

  const columns = [
    { key: 'category', label: 'Category' },
    { key: 'question', label: 'Question' },
    { key: 'answer', label: 'Answer' },
  ];

  return (
    <div>
      <div className="section-action-bar">
        <div>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 700 }}>Frequently Asked Questions</h3>
          <p className="section-desc" style={{ margin: '4px 0 0 0' }}>Manage FAQ questions and answers in English, Marathi, and Kannada.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button className="btn-add" onClick={openAdd}><FaPlus /> Add FAQ</button>
          <span className="badge-count">{items.length} FAQs</span>
        </div>
      </div>
      <CrudTable
        items={items} loading={loading} columns={columns}
        onEdit={openEdit}
        onDelete={async id => { await deleteFAQ(id); refresh(); }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit FAQ' : 'Add FAQ'}>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="mf-group">
            <label>Category</label>
            <select className="mf-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              {categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>

          <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#1e293b' }}>🇬🇧 English (Required)</h4>
            <div className="mf-group">
              <label>Question (English) *</label>
              <input className="mf-control" required value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} placeholder="Enter question in English" />
            </div>
            <div className="mf-group">
              <label>Answer (English) *</label>
              <textarea className="mf-control" required rows={3} value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} placeholder="Enter answer in English" />
            </div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                borderRadius: '8px',
                background: '#e0f2fe',
                color: '#0369a1',
                border: '1px solid #bae6fd',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.88rem'
              }}
            >
              {translating ? 'Translating...' : '✨ Auto-Translate to Marathi & Kannada'}
            </button>
          </div>

          <div style={{ background: '#fefce8', padding: '14px', borderRadius: '10px', border: '1px solid #fef08a', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#854d0e' }}>🚩 मराठी (Marathi)</h4>
            <div className="mf-group">
              <label>प्रश्न (मराठी)</label>
              <input className="mf-control" value={form.question_mr} onChange={e => setForm({ ...form, question_mr: e.target.value })} placeholder="मराठीत प्रश्न टाका (किंवा आपोआप भाषांतरित होईल)" />
            </div>
            <div className="mf-group">
              <label>उत्तर (मराठी)</label>
              <textarea className="mf-control" rows={3} value={form.answer_mr} onChange={e => setForm({ ...form, answer_mr: e.target.value })} placeholder="मराठीत उत्तर टाका (किंवा आपोआप भाषांतरित होईल)" />
            </div>
          </div>

          <div style={{ background: '#fdf2f8', padding: '14px', borderRadius: '10px', border: '1px solid #fbcfe8', marginBottom: '14px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', color: '#9d174d' }}>🟡 ಕನ್ನಡ (Kannada)</h4>
            <div className="mf-group">
              <label>ಪ್ರಶ್ನೆ (ಕನ್ನಡ)</label>
              <input className="mf-control" value={form.question_kn} onChange={e => setForm({ ...form, question_kn: e.target.value })} placeholder="ಕನ್ನಡದಲ್ಲಿ ಪ್ರಶ್ನೆ ನಮೂದಿಸಿ (ಅಥವಾ ಸ್ವಯಂ ಅನುವಾದಗೊಳ್ಳುತ್ತದೆ)" />
            </div>
            <div className="mf-group">
              <label>ಉತ್ತರ (ಕನ್ನಡ)</label>
              <textarea className="mf-control" rows={3} value={form.answer_kn} onChange={e => setForm({ ...form, answer_kn: e.target.value })} placeholder="ಕನ್ನಡದಲ್ಲಿ ಉತ್ತರ ನಮೂದಿಸಿ (ಅಥವಾ ಸ್ವಯಂ ಅನುವಾದಗೊಳ್ಳುತ್ತದೆ)" />
            </div>
          </div>

          <div className="mf-actions">
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : <><FaCheck /> {editing ? 'Update' : 'Add'} FAQ</>}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};



// ====================== DASHBOARD OVERVIEW ======================
const DashboardOverview = ({ setActiveTab }) => {
  const [stats, setStats] = useState({ products: 0, services: 0, blogs: 0, productEnquiries: 0, generalEnquiries: 0, careers: 0, testimonials: 0, faqs: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getProducts(), getServices(), getBlogs(), getEnquiries(), getCareerApplications(), getTestimonials(), getFAQs()]).then(([p, s, b, e, c, t, f]) => {
      const prodEnq = e.filter(item => item.productName || item.type === 'product' || item.enquiryType === 'product');
      const genEnq = e.filter(item => !item.productName && item.type !== 'product' && item.enquiryType !== 'product');
      setStats({
        products: p.length,
        services: s.length,
        blogs: b.length,
        productEnquiries: prodEnq.length,
        generalEnquiries: genEnq.length,
        careers: c.length,
        testimonials: t.length,
        faqs: f.length
      });
      setLoading(false);
    });
  }, []);

  const cards = [
    { label: 'Products', value: stats.products, icon: <FaBoxOpen />, tab: 'products', color: '#04416d', action: 'Manage Products' },
    { label: 'Services', value: stats.services, icon: <FaClipboardList />, tab: 'services', color: '#0b5c96', action: 'Manage Services' },
    { label: 'Product Enquiry', value: stats.productEnquiries, icon: <FaIndustry />, tab: 'product_enquiries', color: '#059669', action: 'View Enquiries' },
    { label: 'General Enquiry', value: stats.generalEnquiries, icon: <FaEnvelope />, tab: 'enquiries', color: '#d4294a', action: 'View Enquiries' },
    { label: 'Blog Posts', value: stats.blogs, icon: <FaBlog />, tab: 'blogs', color: '#e33355', action: 'Manage Blogs' },
    { label: 'Testimonials', value: stats.testimonials, icon: <FaQuoteLeft />, tab: 'testimonials', color: '#d97706', action: 'Manage Reviews' },
    { label: 'FAQs', value: stats.faqs, icon: <FaQuestionCircle />, tab: 'faqs', color: '#2563eb', action: 'Manage FAQs' },
    { label: 'Careers', value: stats.careers, icon: <FaBriefcase />, tab: 'careers', color: '#7c3aed', action: 'View Applications' },
  ];

  return (
    <div>
      {/* Welcome Banner */}
      <div className="dashboard-banner">
        <div className="dashboard-banner-content">
          <div className="dashboard-banner-badge">
            <span className="pulse-dot"></span> Admin Control Center
          </div>
          <h2>Welcome back! 👋</h2>
          <p>Here is a real-time snapshot of your website content and customer inquiries.</p>
        </div>
        <div className="dashboard-banner-stats">
          <div className="banner-kpi-pill">
            <span className="bk-val">{loading ? '—' : stats.productEnquiries + stats.generalEnquiries}</span>
            <span className="bk-lbl">New Inquiries</span>
          </div>
          <div className="banner-kpi-pill">
            <span className="bk-val">{loading ? '—' : stats.products}</span>
            <span className="bk-lbl">Total Products</span>
          </div>
        </div>
      </div>

      {/* Stats Cards - Perfectly Aligned 4x2 Grid */}
      <div className="admin-stats">
        {cards.map((c) => (
          <div
            className="admin-stat-card"
            key={c.label}
            onClick={() => setActiveTab(c.tab)}
            style={{ '--card-color': c.color }}
          >
            <div className="stat-card-top">
              <div className="stat-card-icon" style={{ background: `${c.color}15`, color: c.color }}>
                {c.icon}
              </div>
              <span className="stat-card-action-badge">
                <span>{c.action}</span>
                <FaArrowRight className="stat-arrow-icon" />
              </span>
            </div>
            <div className="stat-card-main">
              <div className="stat-number">{loading ? '—' : c.value}</div>
              <div className="stat-card-label">{c.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ====================== MAIN DASHBOARD ======================
export const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => { requireAuth(navigate); }, [navigate]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardOverview setActiveTab={setActiveTab} />;
      case 'products': return <ProductManager />;
      case 'services': return <ServiceManager />;
      case 'blogs': return <BlogManager />;
      case 'testimonials': return <TestimonialsManager />;
      case 'faqs': return <FAQManager />;
      case 'product_enquiries': return <ProductEnquiriesManager />;
      case 'enquiries': return <GeneralEnquiriesManager />;
      case 'careers': return <CareersManager />;
      default: return <DashboardOverview setActiveTab={setActiveTab} />;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      {renderContent()}
    </AdminLayout>
  );
};

// ====================== LOGIN PAGE ======================
export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('adminAuth')) navigate('/admin/dashboard');
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    await new Promise(r => setTimeout(r, 700));

    // Bypass validation entirely as per user request to directly open admin panel without errors
    localStorage.setItem('adminAuth', 'true');
    navigate('/admin/dashboard');
  };

  return (
    <div className="admin-login-page">
      {/* Left Panel */}
      <div className="login-left">
        <div className="l-shapes">
          <div className="l-shape ls-1" /><div className="l-shape ls-2" /><div className="l-shape ls-3" />
        </div>
        <div className="login-left-content">
          <div className="login-logo-box"><Logo width="200px" height="62px" isLight={true} /></div>
          <h1 className="login-hero-text">Manage Your<br />Business, Effortlessly</h1>
          <p className="login-hero-sub">The all-in-one admin dashboard for Digvijay Industries — manage products, services, blogs, and enquiries in one place.</p>
          <div className="login-features">
            {['Dynamic Product & Service Management', 'Real-time Blog Publishing', 'Enquiry & Career Application Tracking', 'Instant Website Updates (No Reload)'].map(f => (
              <div key={f} className="login-feature-item"><FaCheck /><span>{f}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="login-right">
        <div className="login-form-box">
          <div className="login-form-header">
            <div className="login-avatar"><FaTachometerAlt /></div>
            <h2>Admin Sign In</h2>
            <p>Enter your credentials to access the dashboard</p>
          </div>

          {error && <div className="login-error"><FaTimes />{error}</div>}

          <form onSubmit={handleLogin} className="login-form">
            <div className="login-field">
              <label>Email Address</label>
              <input type="email" required className="login-input" placeholder="digvijayindustries@gmail.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="login-field">
              <label>Password</label>
              <div className="password-wrap">
                <input type={showPass ? 'text' : 'password'} required className="login-input" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} />
                <button type="button" className="toggle-pass" onClick={() => setShowPass(!showPass)}>
                  <FaEye />
                </button>
              </div>
            </div>
            <button type="submit" className={`login-submit-btn ${loading ? 'loading' : ''}`} disabled={loading}>
              {loading ? <span className="spinner" /> : 'Sign In to Dashboard →'}
            </button>
          </form>
          <p className="login-footer-note">Digvijay Industries © {new Date().getFullYear()} · Authorized Access Only</p>
        </div>
      </div>
    </div>
  );
};
